import {
  Autocomplete,
  AutocompleteOption,
  AutocompleteProps,
  FormControl,
  FormHelperText,
  FormLabel,
  ListItemContent,
  Typography,
} from '@mui/joy';
import { useQuery } from '@tanstack/react-query';
import { ReactNode } from 'react';

import { I18N_DE } from '@/i18n/de';
import { Organization } from '@/modules/api';
import { backendApiOrganizationsFindAllOptions } from '@/modules/api/@tanstack/react-query.gen';

type OrganizationAutocompleteProps<
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> = Omit<
  AutocompleteProps<Organization, Multiple, DisableClearable, FreeSolo>,
  'options' | 'renderOption' | 'getOptionLabel' | 'placeholder' | 'loadingText' | 'noOptionsText'
> & {
  excludeIds?: string[];
  label?: string;
  helperText?: ReactNode;
  activeOnly?: boolean;
};

export function OrganizationAutocomplete<
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>({
  excludeIds = [],
  label,
  helperText,
  activeOnly,
  error,
  ...props
}: OrganizationAutocompleteProps<Multiple, DisableClearable, FreeSolo>) {
  const { data: organizations = [], isLoading } = useQuery(
    backendApiOrganizationsFindAllOptions({ query: { ...(activeOnly && { active: activeOnly }) } }),
  );

  return (
    <FormControl error={error}>
      {label && <FormLabel>{label}</FormLabel>}
      <Autocomplete<Organization, Multiple, DisableClearable, FreeSolo>
        aria-label={label ?? 'Organisation suchen'}
        loading={isLoading}
        options={organizations.filter((organization) => !excludeIds.includes(organization.id))}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderOption={(renderOptionProps, option) => (
          <AutocompleteOption {...renderOptionProps} key={option.id}>
            <ListItemContent>
              <Typography level="body-sm">{I18N_DE.OrganizationType[option.type]}</Typography>
              <Typography fontWeight="bold">{option.name}</Typography>
            </ListItemContent>
          </AutocompleteOption>
        )}
        getOptionLabel={(option) => {
          if (typeof option === 'string') {
            return organizations.find(({ id }) => id === option)?.name || '';
          }

          return `${option.name} (${I18N_DE.OrganizationType[option.type]})`;
        }}
        placeholder="Suchen"
        loadingText="Laden..."
        noOptionsText="Kein Ergebnis"
        {...props}
      />
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
}
